import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

const FONT_NAMES = [
  'Arial',
  'Verdana',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Trebuchet MS',
  'Segoe UI',
  'Roboto',
]

const FONT_SIZES = [
  { value: '1', label: '10px' },
  { value: '2', label: '13px' },
  { value: '3', label: '16px' },
  { value: '4', label: '18px' },
  { value: '5', label: '24px' },
  { value: '6', label: '32px' },
  { value: '7', label: '48px' },
]

function ToolbarButton({ icon, title, onClick, disabled = false, active = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-on-surface-variant transition-colors cursor-pointer select-none disabled:opacity-35 disabled:cursor-not-allowed ${
        active
          ? 'bg-primary/15 text-primary font-bold'
          : 'hover:bg-surface-container-high hover:text-on-surface'
      }`}
    >
      <span className="material-symbols-outlined text-[17px] leading-none">{icon}</span>
    </button>
  )
}

function ToolbarDivider() {
  return <span className="w-px h-5 bg-outline-variant/30 mx-1 self-center" />
}

const AppRichTextEditor = forwardRef(function AppRichTextEditor(
  {
    value = '',
    onChange,
    height = 400,
    disabled = false,
    placeholder = 'Enter content...',
    className = '',
    showModeTabs = true,
    toolbarExtra = null,
  },
  ref,
) {
  const editorRef = useRef(null)
  const sourceRef = useRef(null)
  const lastRangeRef = useRef(null)

  const [mode, setMode] = useState('design') // 'design' | 'source' | 'preview'
  const [sourceText, setSourceText] = useState(value || '')
  const [fontName, setFontName] = useState('Arial')
  const [fontSize, setFontSize] = useState('3')

  // Keep source text in sync with external value
  useEffect(() => {
    setSourceText(value || '')
  }, [value])

  // Synchronize design editable element
  useEffect(() => {
    if (editorRef.current && mode === 'design') {
      if (editorRef.current.innerHTML !== (value || '')) {
        editorRef.current.innerHTML = value || ''
      }
    }
  }, [value, mode])

  // Save selection range for Design mode so clicking external buttons (like TIPS) doesn't lose cursor position
  const saveSelection = useCallback(() => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0 && editorRef.current) {
      const range = sel.getRangeAt(0)
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        lastRangeRef.current = range.cloneRange()
      }
    }
  }, [])

  const restoreSelection = useCallback(() => {
    const sel = window.getSelection()
    if (sel && lastRangeRef.current && editorRef.current) {
      sel.removeAllRanges()
      sel.addRange(lastRangeRef.current)
    }
  }, [])

  // Execute formatting command
  const execCmd = useCallback(
    (command, commandValue = null) => {
      if (disabled || mode !== 'design') return
      editorRef.current?.focus()
      restoreSelection()
      document.execCommand(command, false, commandValue)
      saveSelection()
      if (editorRef.current && onChange) {
        onChange(editorRef.current.innerHTML)
      }
    },
    [disabled, mode, onChange, restoreSelection, saveSelection],
  )

  // Direct HTML insertion
  const insertHtmlContent = useCallback(
    (htmlToInsert) => {
      if (disabled || !htmlToInsert) return

      if (mode === 'source') {
        const textarea = sourceRef.current
        if (textarea) {
          const start = textarea.selectionStart ?? 0
          const end = textarea.selectionEnd ?? 0
          const current = textarea.value || ''
          const updated = current.slice(0, start) + htmlToInsert + current.slice(end)
          setSourceText(updated)
          if (onChange) onChange(updated)
          setTimeout(() => {
            textarea.focus()
            textarea.selectionStart = textarea.selectionEnd = start + htmlToInsert.length
          }, 0)
        }
      } else {
        // Design mode
        if (mode !== 'design') setMode('design')
        setTimeout(() => {
          editorRef.current?.focus()
          restoreSelection()
          document.execCommand('insertHTML', false, htmlToInsert)
          saveSelection()
          if (editorRef.current && onChange) {
            onChange(editorRef.current.innerHTML)
          }
        }, 10)
      }
    },
    [disabled, mode, onChange, restoreSelection, saveSelection],
  )

  // Forwarded imperative API
  useImperativeHandle(
    ref,
    () => ({
      insertHtml: (html) => insertHtmlContent(html),
      insertTag: (tag) => insertHtmlContent(tag),
      getHtml: () => (mode === 'source' ? sourceText : editorRef.current?.innerHTML || value || ''),
      setHtml: (newHtml) => {
        setSourceText(newHtml || '')
        if (editorRef.current) editorRef.current.innerHTML = newHtml || ''
        if (onChange) onChange(newHtml || '')
      },
      focus: () => {
        if (mode === 'source') sourceRef.current?.focus()
        else editorRef.current?.focus()
      },
    }),
    [insertHtmlContent, mode, sourceText, value, onChange],
  )

  const handleInput = useCallback(() => {
    if (!editorRef.current) return
    const currentHtml = editorRef.current.innerHTML
    setSourceText(currentHtml)
    if (onChange) onChange(currentHtml)
    saveSelection()
  }, [onChange, saveSelection])

  const handleSourceChange = useCallback(
    (e) => {
      const val = e.target.value
      setSourceText(val)
      if (onChange) onChange(val)
    },
    [onChange],
  )

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return

    if (mode === 'design') {
      const currentHtml = editorRef.current?.innerHTML || value || ''
      setSourceText(currentHtml)
    } else if (mode === 'source') {
      if (onChange) onChange(sourceText)
    }

    setMode(newMode)
  }

  const handleLink = async () => {
    if (disabled) return
    const url = await appPrompt({
      title: 'Insert Web Link',
      message: 'Enter web link URL:',
      defaultValue: 'https://',
      confirmText: 'Insert Link',
    })
    if (url) execCmd('createLink', url)
  }

  const handleInsertTable = async () => {
    if (disabled) return
    const rows = await appPrompt({
      title: 'Insert Table',
      message: 'Number of rows:',
      defaultValue: '3',
      inputType: 'number',
      confirmText: 'Next',
    })
    if (!rows) return
    const cols = await appPrompt({
      title: 'Insert Table',
      message: 'Number of columns:',
      defaultValue: '3',
      inputType: 'number',
      confirmText: 'Insert Table',
    })
    if (!cols) return
    const numRows = parseInt(rows, 10)
    const numCols = parseInt(cols, 10)
    if (!isNaN(numRows) && !isNaN(numCols) && numRows > 0 && numCols > 0) {
      let html = '<table style="width:100%;border-collapse:collapse;margin:8px 0;font-size:13px;">'
      html += '<thead><tr>'
      for (let c = 0; c < numCols; c++) {
        html += `<th style="border:1px solid #cbd5e1;padding:6px 10px;background:#f1f5f9;font-weight:600;">Header ${c + 1}</th>`
      }
      html += '</tr></thead><tbody>'
      for (let r = 0; r < numRows; r++) {
        html += '<tr>'
        for (let c = 0; c < numCols; c++) {
          html += '<td style="border:1px solid #cbd5e1;padding:6px 10px;">&nbsp;</td>'
        }
        html += '</tr>'
      }
      html += '</tbody></table><p><br></p>'
      insertHtmlContent(html)
    }
  }

  const handlePasteClean = async () => {
    if (disabled) return
    const text = await appPrompt({
      title: 'Paste Clean Text',
      message: 'Paste plain text here:',
      placeholder: 'Type or paste plain text...',
      confirmText: 'Paste',
    })
    if (text) {
      const sanitized = text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')
      insertHtmlContent(sanitized)
    }
  }

  const dropdownStyle =
    'h-7 rounded-lg border border-outline-variant/30 bg-surface-container-low px-2 text-xs font-semibold text-on-surface outline-none transition-colors focus:border-primary disabled:opacity-40 cursor-pointer'

  return (
    <div
      className={`flex flex-col rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden shadow-xs ${className}`}
    >
      {/* ── MD3 Toolbar ──────────────────────────────────────────────────────── */}
      <div
        className="flex flex-wrap items-center gap-1 border-b border-outline-variant/20 bg-surface-container-low/70 px-3 py-2 select-none"
        style={{ opacity: disabled ? 0.6 : 1 }}
      >
        {/* Undo / Redo */}
        <ToolbarButton icon="undo" title="Undo" onClick={() => execCmd('undo')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="redo" title="Redo" onClick={() => execCmd('redo')} disabled={disabled || mode !== 'design'} />

        <ToolbarDivider />

        {/* Text Formats */}
        <ToolbarButton icon="format_bold" title="Bold (Ctrl+B)" onClick={() => execCmd('bold')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_italic" title="Italic (Ctrl+I)" onClick={() => execCmd('italic')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_underlined" title="Underline (Ctrl+U)" onClick={() => execCmd('underline')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="strikethrough_s" title="Strikethrough" onClick={() => execCmd('strikeThrough')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="subscript" title="Subscript" onClick={() => execCmd('subscript')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="superscript" title="Superscript" onClick={() => execCmd('superscript')} disabled={disabled || mode !== 'design'} />

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton icon="format_align_left" title="Align Left" onClick={() => execCmd('justifyLeft')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_align_center" title="Align Center" onClick={() => execCmd('justifyCenter')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_align_right" title="Align Right" onClick={() => execCmd('justifyRight')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_align_justify" title="Justify" onClick={() => execCmd('justifyFull')} disabled={disabled || mode !== 'design'} />

        <ToolbarDivider />

        {/* Lists & Indentation */}
        <ToolbarButton icon="format_list_numbered" title="Numbered List" onClick={() => execCmd('insertOrderedList')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_list_bulleted" title="Bullet List" onClick={() => execCmd('insertUnorderedList')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_indent_decrease" title="Outdent" onClick={() => execCmd('outdent')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_indent_increase" title="Indent" onClick={() => execCmd('indent')} disabled={disabled || mode !== 'design'} />

        <ToolbarDivider />

        {/* Links & Clear */}
        <ToolbarButton icon="link" title="Insert Link" onClick={handleLink} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="link_off" title="Remove Link" onClick={() => execCmd('unlink')} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="format_clear" title="Clear Formatting" onClick={() => execCmd('removeFormat')} disabled={disabled || mode !== 'design'} />

        <ToolbarDivider />

        {/* Text Color */}
        <label
          className="flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
          title="Text Color"
        >
          <span className="material-symbols-outlined text-[17px] text-on-surface-variant">format_color_text</span>
          <input
            type="color"
            defaultValue="#000000"
            onChange={(e) => execCmd('foreColor', e.target.value)}
            disabled={disabled || mode !== 'design'}
            className="w-5 h-5 rounded border border-outline-variant/40 p-0 cursor-pointer bg-transparent disabled:cursor-not-allowed"
          />
        </label>

        {/* Highlight Color */}
        <label
          className="flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
          title="Highlight Background Color"
        >
          <span className="material-symbols-outlined text-[17px] text-on-surface-variant">format_color_fill</span>
          <input
            type="color"
            defaultValue="#ffff00"
            onChange={(e) => execCmd('hiliteColor', e.target.value)}
            disabled={disabled || mode !== 'design'}
            className="w-5 h-5 rounded border border-outline-variant/40 p-0 cursor-pointer bg-transparent disabled:cursor-not-allowed"
          />
        </label>

        <ToolbarDivider />

        {/* Font Family */}
        <select
          value={fontName}
          onChange={(e) => {
            setFontName(e.target.value)
            execCmd('fontName', e.target.value)
          }}
          disabled={disabled || mode !== 'design'}
          className={`${dropdownStyle} w-32`}
          title="Font Family"
        >
          {FONT_NAMES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        {/* Font Size */}
        <select
          value={fontSize}
          onChange={(e) => {
            setFontSize(e.target.value)
            execCmd('fontSize', e.target.value)
          }}
          disabled={disabled || mode !== 'design'}
          className={`${dropdownStyle} w-20`}
          title="Font Size"
        >
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <ToolbarDivider />

        {/* Table & Paste */}
        <ToolbarButton icon="table_chart" title="Insert Table" onClick={handleInsertTable} disabled={disabled || mode !== 'design'} />
        <ToolbarButton icon="content_paste" title="Paste Clean Text" onClick={handlePasteClean} disabled={disabled || mode !== 'design'} />

        {/* Custom Toolbar Extra Slot */}
        {toolbarExtra && (
          <>
            <ToolbarDivider />
            <div className="flex items-center gap-1">{toolbarExtra}</div>
          </>
        )}
      </div>

      {/* ── Editor Canvas Area ──────────────────────────────────────────────── */}
      <div className="relative bg-surface-container-lowest" style={{ minHeight: `${height}px` }}>
        {/* DESIGN Mode */}
        {mode === 'design' && (
          <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyUp={saveSelection}
            onMouseUp={saveSelection}
            data-placeholder={placeholder}
            style={{
              minHeight: `${height}px`,
              maxHeight: `${Math.max(height, 500)}px`,
            }}
            className="w-full overflow-y-auto px-5 py-4 text-sm leading-relaxed text-on-surface outline-none empty:before:text-on-surface-variant/40 empty:before:content-[attr(data-placeholder)] select-text"
          />
        )}

        {/* SOURCE Mode */}
        {mode === 'source' && (
          <textarea
            ref={sourceRef}
            value={sourceText}
            onChange={handleSourceChange}
            disabled={disabled}
            style={{
              minHeight: `${height}px`,
              maxHeight: `${Math.max(height, 500)}px`,
            }}
            className="w-full overflow-y-auto resize-none border-0 bg-surface-container-lowest px-5 py-4 font-mono text-xs leading-relaxed text-on-surface outline-none tab-2"
            placeholder="Edit raw HTML source code here..."
            spellCheck={false}
          />
        )}

        {/* PREVIEW Mode */}
        {mode === 'preview' && (
          <div
            style={{
              minHeight: `${height}px`,
              maxHeight: `${Math.max(height, 500)}px`,
            }}
            className="w-full overflow-y-auto border-0 bg-white px-6 py-5 text-sm leading-relaxed text-slate-800 shadow-inner"
            dangerouslySetInnerHTML={{ __html: sourceText || '<p class="text-slate-400 italic">No content to preview.</p>' }}
          />
        )}
      </div>

      {/* ── Mode Switcher Tabs (Design / Source / Preview) ───────────────────── */}
      {showModeTabs && (
        <div className="flex items-center justify-between border-t border-outline-variant/20 bg-surface-container-low/50 px-3 py-1.5 text-xs">
          <div className="flex items-center gap-1">
            {[
              { key: 'design', label: 'Design', icon: 'edit_note' },
              { key: 'source', label: 'HTML Source', icon: 'code' },
              { key: 'preview', label: 'Preview', icon: 'visibility' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleModeSwitch(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                  mode === tab.key
                    ? 'bg-surface-container-highest text-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                }`}
              >
                <span className="material-symbols-outlined text-sm">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
          <div className="text-[11px] font-semibold text-on-surface-variant/60">
            {mode === 'design' ? 'WYSIWYG Mode' : mode === 'source' ? 'HTML Mode' : 'Live Render'}
          </div>
        </div>
      )}
    </div>
  )
})

export default AppRichTextEditor

